// TODO: rename to something like StudyRegistriesProtocolMaintenancePanel
import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import GridTypeA from "components/grids/GridTypeA/GridTypeA"
import useSiteUploadActions from "components/useSiteUploadActions/useSiteUploadActions"
import { Panel, Switch } from "components-design-system"
import { REGENERATE_TASKS } from "constants/tasks"

import "./ProtocolMaintenance.scss"

// cell renderers
import Compare from "../CellRenderers/Compare"

// context
import useAppState from "context/hooks/useAppState"
import useAuthoringStore from "../../../Authoring/hooks/stores/useAuthoringStore"

// api
import { useUserPermissions } from "api/hooks"
import { useQueryClient } from "@tanstack/react-query"
import queryKeys from "api/utils/queryKeys"
import { useGetAuthoringStudy } from "api/hooks/studies/useAuthoringApi"
import {
  usePostPrsProtocolDownload,
  useGetPrsProtocolUpload,
  usePostPrsProtocolUpload,
} from "api/hooks/studies/useRegistries"
import { PRS_UPLOAD_TASKS } from "../../../../../../../constants/tasks"
import { useGetEnvVars } from "api/hooks/enum"
import { isEqual } from "lodash"

const ProtocolMaintenance = ({ study, protocolDataCompare }) => {
  const { studyId } = useParams()
  const queryClient = useQueryClient()
  const { setPollingTaskId, setTaskType, pollingCallbackRef } = useAppState()

  const authoringId = useAuthoringStore((state) => state.authoringId)

  const { data: authoring } = useGetAuthoringStudy(authoringId)
  const { data: permissions } = useUserPermissions()
  const { data: envVars } = useGetEnvVars()

  const [queueId, setQueueId] = useState(null)
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(true)

  const postPrsProtocolDownload = usePostPrsProtocolDownload(study?.authoring_study_id)

  const { data: pollingData } = useGetPrsProtocolUpload(
    { queueId },
    {
      refetchInterval: queueId ? 1000 : null,
      enabled: queueId ? true : false,
    }
  )

  useEffect(() => {
    if (pollingData?.in_progress === false || pollingData?.is_cancelled === true) {
      onStopPolling()
    }
  }, [pollingData?.in_progress])

  const gridOptions = {
    columnDefs: [
      {
        headerName: "Field",
        field: "field_label",
      },
      {
        headerName: "Prime",
        field: "prime_val",
        cellRenderer: Compare,
      },
      {
        headerName: "CTG",
        field: "prs_val",
        cellRenderer: Compare,
      },
    ],
    defaultColDef: {
      wrapText: true,
      filter: true,
      filterParams: {
        buttons: ["clear"],
      },
      enablePivot: true,
      enableCharts: false,
      enableRowGroup: false,
      cellStyle: { whiteSpace: "normal !important" },
      autoHeight: true,
    },
  }

  const onGridReady = (params) => {
    params.api.sizeColumnsToFit()
  }

  const onRegenerate = () => {
    postPrsProtocolDownload(
      {},
      {
        onSuccess: (resp) => {
          setPollingTaskId(resp?.data?.task_id)
          setTaskType(REGENERATE_TASKS.studyRegistry)
          pollingCallbackRef.current = (pollingData) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.registriesData.details(studyId) })
            queryClient.invalidateQueries({
              queryKey: queryKeys.registriesDataCompare.details(study?.authoring_study_id),
            })
          }
        },
      }
    )
  }

  const onStopPolling = () => {
    setQueueId(null)
  }
  
  const { RegenerateButton, UploadButton, UploadModals, isUploading } = useSiteUploadActions({
    prsUploadStudyList: [Number(study?.authoring_study_id)],
    useOnGetPrsUpload: useGetPrsProtocolUpload,
    useOnPostPrsUpload: usePostPrsProtocolUpload,
    canUploadToPRS: permissions?.["study.protocolmaintenance.upload"] ,
    isUploadDisabled: envVars?.DISABLE_PROTOCOL_MAINTENANCE_SUBMISSIONS,
    onRegenerate: onRegenerate,
    metadata: {},
    source: "study-registry",
    lastImportDate: authoring?.ctg_protocol_download_date,
    type: PRS_UPLOAD_TASKS.studyRegistryPrsProtocolUpload,
    disabledTooltip:envVars?.DISABLE_PROTOCOL_MAINTENANCE_SUBMISSIONS ? "Submission of live/unapproved data has been disabled. Please contact organization admin for details."  :"Submission of live/unapproved data has been disabled. Please contact organization admin for details."
  })

  const actions = [
    <Switch
      checked={showOnlyDifferences}
      onChange={() => setShowOnlyDifferences((prev) => !prev)}
      label={"Show Differences Only"}
      wrapperClassName="show-only-differences-switch"
      className="show-label"
      disabled={isUploading}
    />,
    RegenerateButton,
  ]
  if (permissions?.["study.protocolmaintenance.upload"]) {
    actions.push(UploadButton)
  }

  const normalizeData = (val) =>
    val === null || val === undefined || (Array.isArray(val) && val.length === 0) || val === "" ? null : val

  const getUniqueFields = () => {
    const areOtherNamesDifferent = (prime, prs) => {
      if (prime === null && normalizeData(prs) === null) return false

      //if prs other_intervention_names is an array that means prime is either null or array, so compare them
      if (Array.isArray(prs)) {
        return !isEqual(prime, prs)
      }
      //if prs is not an array that means its either null or just a string with name in it
      if (Array.isArray(prime)) {
        if (prime.length > 1) return true //if primes other_intervention_names is greater than 1 then that means prs is a string and prime is ann array with leanth>0 so there is a difference
        return !isEqual(prime[0], prs) //if  prime length is 1 then compare prime[0] and the string
      }

      return !isEqual(prime, prs)
    }

    const areInterventionsDifferent = (prime, prs) => {
      if (normalizeData(prime) === normalizeData(prs)) return false //normalising for values such as [] so it will be trated as null
      if ((Array.isArray(prime) && !Array.isArray(prs)) || (Array.isArray(prs) && !Array.isArray(prime))) return true //if only one of them is an array return true
      if (Array.isArray(prime) && Array.isArray(prs) && prime?.length !== prs?.length) return true //If both are arrays and have different lengths show them because they are different

      //if prs and prime are of same length compare their data
      return prime?.some((primeItem, index) => {
        const prsItem = prs?.[index]

        return (
          !isEqual(primeItem?.name, prsItem?.name) ||
          !isEqual(primeItem?.type, prsItem?.type) ||
          !isEqual(primeItem?.description, prsItem?.description) ||
          areOtherNamesDifferent(primeItem?.other_intervention_names, prsItem?.other_intervention_names)
        )
      })
    }

    return protocolDataCompare.filter((data) => {
      const { field_label, prime_val, prs_val } = data

      if (field_label !== "Intervention") {
        return !isEqual(normalizeData(prime_val), normalizeData(prs_val))
      }

      return areInterventionsDifferent(prime_val, prs_val)
    })
  }

  return (
    <Panel
      title="PROTOCOL MAINTENANCE"
      iconClassName="fa-light fa-hammer"
      style={{ flex: 2 }}
      actions={actions}
      className="study-protocol-maintenance-panel"
    >
      {showOnlyDifferences && getUniqueFields().length === 0 ? (
        <div className="no-differences-msg-container">
          There are no differences between Prime and CTG data. Toggle “Show Differences Only” OFF to display all fields.
        </div>
      ) : (
        <div
          className="ag-theme-quartz reg-pm-grid-wrap"
          id="ag-theme-prime-disabled"
        >
          <GridTypeA
            onGridReady={onGridReady}
            gridOptions={gridOptions}
            domLayout="autoHeight"
            rowData={showOnlyDifferences ? getUniqueFields() : protocolDataCompare}
            className="study-data-protocol-maintenance"
            autoSizeStrategy="fitGridWidth"
            showRowCount={false}
          />
        </div>
      )}

      {UploadModals}
    </Panel>
  )
}

export default ProtocolMaintenance
